import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { CardsSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import { formatAmountMinor, resolveFinanceLocale } from './finance-format';
import {
  isNullableNumber,
  isNullableNumberArray,
  isNullableString,
  isNumberArray,
  isRecord,
} from './finance-view-guards';
import { useFinanceView } from './useFinanceView';

type CashFlowLine = { monthlyMinor: number[]; totalMinor: number };
type CashFlowRow = CashFlowLine & { key: string; label: string; hint: string | null };

type CashFlow = {
  organizationId: string;
  fiscalYear: number;
  availableYears: number[];
  months: Array<{ key: string; actual: boolean }>;
  inflowRows: CashFlowRow[];
  outflowGroups: Array<{ groupLabel: string; rows: CashFlowRow[] }>;
  totals: {
    inflow: CashFlowLine;
    operatingOutflow: CashFlowLine;
    nonEconomicOutflow: CashFlowLine;
    outflow: CashFlowLine;
    netFlow: CashFlowLine;
  };
  balance: {
    monthlyMinor: (number | null)[];
    closingMinor: number | null;
    anchor: {
      observedOn: string;
      amountMinor: number;
      accounts: Array<{ accountLabel: string; observedOn: string; amountMinor: number }>;
    } | null;
    accountsWithoutObservation: string[];
  };
};

function isLine(value: unknown): value is CashFlowLine {
  return (
    isRecord(value) && isNumberArray(value.monthlyMinor) && typeof value.totalMinor === 'number'
  );
}

function isRow(value: unknown): value is CashFlowRow {
  return (
    isLine(value) &&
    typeof (value as CashFlowRow).key === 'string' &&
    typeof (value as CashFlowRow).label === 'string' &&
    isNullableString((value as CashFlowRow).hint)
  );
}

function isOutflowGroup(group: unknown): boolean {
  return (
    isRecord(group) &&
    typeof group.groupLabel === 'string' &&
    Array.isArray(group.rows) &&
    group.rows.every(isRow)
  );
}

function isTotals(totals: unknown): boolean {
  return (
    isRecord(totals) &&
    isLine(totals.inflow) &&
    isLine(totals.operatingOutflow) &&
    isLine(totals.nonEconomicOutflow) &&
    isLine(totals.outflow) &&
    isLine(totals.netFlow)
  );
}

function isAnchor(anchor: unknown): boolean {
  return (
    anchor === null ||
    (isRecord(anchor) &&
      typeof anchor.observedOn === 'string' &&
      typeof anchor.amountMinor === 'number' &&
      Array.isArray(anchor.accounts))
  );
}

function isBalance(balance: unknown): boolean {
  return (
    isRecord(balance) &&
    isNullableNumberArray(balance.monthlyMinor) &&
    isNullableNumber(balance.closingMinor) &&
    isAnchor(balance.anchor) &&
    Array.isArray(balance.accountsWithoutObservation) &&
    balance.accountsWithoutObservation.every((label) => typeof label === 'string')
  );
}

function isCashFlow(value: unknown): value is CashFlow {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.organizationId === 'string' &&
    typeof value.fiscalYear === 'number' &&
    isNumberArray(value.availableYears) &&
    Array.isArray(value.months) &&
    value.months.every(
      (month) =>
        isRecord(month) && typeof month.key === 'string' && typeof month.actual === 'boolean',
    ) &&
    Array.isArray(value.inflowRows) &&
    value.inflowRows.every(isRow) &&
    Array.isArray(value.outflowGroups) &&
    value.outflowGroups.every(isOutflowGroup) &&
    isTotals(value.totals) &&
    isBalance(value.balance)
  );
}

/** The three-letter month labels the grid heads its columns with, from the locale rather than a list. */
function monthLabels(locale: string): string[] {
  const formatter = new Intl.DateTimeFormat(locale === 'it' ? 'it-IT' : 'en-US', {
    month: 'short',
    timeZone: 'UTC',
  });
  return Array.from({ length: 12 }, (_unused, index) =>
    formatter.format(new Date(Date.UTC(2026, index, 1))),
  );
}

type AmountFormat = (valueMinor: number) => string;
type GridProps = {
  readonly grid: CashFlow;
  readonly labels: readonly string[];
  readonly amount: AmountFormat;
};

function renderLine(
  labels: readonly string[],
  amount: AmountFormat,
  label: string,
  line: CashFlowLine,
  emphasis: boolean,
) {
  return (
    <tr key={label} className={emphasis ? 'border-border border-t font-semibold' : ''}>
      <th scope="row" className="py-1.5 text-left font-inherit">
        {label}
      </th>
      {line.monthlyMinor.map((valueMinor, index) => (
        <td key={labels[index]} className="py-1.5 pl-3 text-right tabular-nums">
          {amount(valueMinor)}
        </td>
      ))}
      <td className="py-1.5 pl-3 text-right font-semibold tabular-nums">
        {amount(line.totalMinor)}
      </td>
    </tr>
  );
}

function GridHead({ grid, labels }: Pick<GridProps, 'grid' | 'labels'>) {
  const { t } = useTranslation('finance');
  return (
    <thead>
      <tr className="border-border border-b">
        <th scope="col" className="py-2 text-left font-medium">
          {t('cashFlow.lineColumn')}
        </th>
        {grid.months.map((month, index) => (
          <th key={month.key} scope="col" className="py-2 pl-3 text-right font-medium">
            <span className="block">{labels[index]}</span>
            <span className="text-foreground/60 block text-xs font-normal">
              {t(month.actual ? 'cashFlow.actual' : 'cashFlow.forecast')}
            </span>
          </th>
        ))}
        <th scope="col" className="py-2 pl-3 text-right font-medium">
          {t('cashFlow.totalColumn')}
        </th>
      </tr>
    </thead>
  );
}

function InflowRows({ grid, labels, amount }: GridProps) {
  const { t } = useTranslation('finance');
  return (
    <>
      <tr>
        <th
          scope="row"
          colSpan={14}
          className="text-foreground/60 pt-4 pb-1 text-left text-xs uppercase"
        >
          {t('cashFlow.inflowSection')}
        </th>
      </tr>
      {grid.inflowRows.map((row) => (
        <tr key={row.key}>
          <th scope="row" className="py-1.5 text-left font-normal">
            {row.label}
            {row.hint !== null && (
              <span className="text-foreground/50 block text-xs">{row.hint}</span>
            )}
          </th>
          {row.monthlyMinor.map((valueMinor, index) => (
            <td key={labels[index]} className="py-1.5 pl-3 text-right tabular-nums">
              {amount(valueMinor)}
            </td>
          ))}
          <td className="py-1.5 pl-3 text-right font-semibold tabular-nums">
            {amount(row.totalMinor)}
          </td>
        </tr>
      ))}
    </>
  );
}

function OutflowRows({ grid, labels, amount }: GridProps) {
  return (
    <>
      {grid.outflowGroups.map((group) => (
        <Fragment key={group.groupLabel}>
          <tr>
            <th
              scope="row"
              colSpan={14}
              className="text-foreground/60 pt-4 pb-1 text-left text-xs uppercase"
            >
              {group.groupLabel}
            </th>
          </tr>
          {group.rows.map((row) => (
            <tr key={`${group.groupLabel}-${row.key}`}>
              <th scope="row" className="py-1.5 text-left font-normal">
                {row.label}
              </th>
              {row.monthlyMinor.map((valueMinor, index) => (
                <td key={labels[index]} className="py-1.5 pl-3 text-right tabular-nums">
                  {amount(valueMinor)}
                </td>
              ))}
              <td className="py-1.5 pl-3 text-right font-semibold tabular-nums">
                {amount(row.totalMinor)}
              </td>
            </tr>
          ))}
        </Fragment>
      ))}
    </>
  );
}

function BalanceRow({ grid, labels, amount }: GridProps) {
  const { t } = useTranslation('finance');
  return (
    <tr className="border-border border-t font-semibold">
      <th scope="row" className="py-1.5 text-left font-inherit">
        {t('cashFlow.totals.balance')}
      </th>
      {grid.balance.monthlyMinor.map((valueMinor, index) => (
        <td key={labels[index]} className="py-1.5 pl-3 text-right tabular-nums">
          {valueMinor === null ? '—' : amount(valueMinor)}
        </td>
      ))}
      <td className="py-1.5 pl-3 text-right tabular-nums">
        {grid.balance.closingMinor === null ? '—' : amount(grid.balance.closingMinor)}
      </td>
    </tr>
  );
}

function BalanceNotes({ grid, amount }: Pick<GridProps, 'grid' | 'amount'>) {
  const { t } = useTranslation('finance');
  return (
    <>
      <p className="text-foreground/60 mt-4 text-xs">
        {grid.balance.anchor === null
          ? t('cashFlow.noAnchor')
          : t('cashFlow.anchor', {
              date: grid.balance.anchor.observedOn,
              amount: amount(grid.balance.anchor.amountMinor),
              count: grid.balance.anchor.accounts.length,
            })}
      </p>

      {/*
        THE ACCOUNT WITH NO FIGURE AT ALL, added 2026-09-21 under Human directive of 2026-09-14
        (decision 34): an absent input must be NAMED — which statement, which bank, which period —
        rather than reported as "incomplete data".

        This is not the same thing as the note below it. That one explains a total assembled from
        observations taken on different days. This one says the total is missing an account
        entirely, which is a larger and quieter error: the observation query joined balances to
        accounts, so an account nobody had filed a figure for was not a null in the list, it was
        not IN the list. The sum of the rest was then presented as the cash position.

        With the two real accounts that is the likeliest first state rather than an edge case —
        one loaded, the other not — and it would have read as a complete position understated by a
        whole bank.
      */}
      {grid.balance.accountsWithoutObservation.length > 0 && (
        <p className="mt-1 text-xs text-amber-900" data-testid="cash-flow-accounts-unobserved">
          {t('cashFlow.accountsWithoutBalance', {
            accounts: grid.balance.accountsWithoutObservation.join(', '),
            count: grid.balance.accountsWithoutObservation.length,
          })}
        </p>
      )}

      {/*
        EACH ACCOUNT NAMED, WITH ITS OWN DATE, added 2026-09-14 by Human decision: "la data più
        fresca per ciascun conto, mostrate entrambe".

        The response has always carried this — label, observation date and amount per account — and
        the page rendered only the total and HOW MANY accounts it came from. A reader could see
        "across 2 accounts" and had no way to learn which two, when each was last observed, or that
        one of them was three days staler than the other.

        That is not cosmetic here. The two real accounts close on different days: the Italian
        statement runs into the first days of the following month while the foreign one closes on the
        last. So the total is the position on the anchor date only for the accounts observed that
        day, and the line below says exactly that when the dates differ — rather than letting one
        date stand for a figure it does not fully describe.
      */}
      {grid.balance.anchor !== null && grid.balance.anchor.accounts.length > 0 && (
        <div className="text-foreground/60 mt-1 text-xs">
          <span className="font-medium">{t('cashFlow.accountsHeading')}</span>
          <ul className="mt-0.5 list-disc pl-5">
            {grid.balance.anchor.accounts.map((account) => (
              <li key={`${account.accountLabel}-${account.observedOn}`}>
                {t('cashFlow.accountLine', {
                  label: account.accountLabel,
                  amount: amount(account.amountMinor),
                  date: account.observedOn,
                })}
              </li>
            ))}
          </ul>
          {new Set(grid.balance.anchor.accounts.map((a) => a.observedOn)).size > 1 && (
            <p className="mt-1">
              {t('cashFlow.mixedDates', { date: grid.balance.anchor.observedOn })}
            </p>
          )}
        </div>
      )}
    </>
  );
}

function YearSelector({
  grid,
  onSelect,
}: {
  readonly grid: CashFlow;
  readonly onSelect: (year: number) => void;
}) {
  const { t } = useTranslation('finance');
  return (
    <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={t('cashFlow.yearSelector')}>
      {grid.availableYears.map((year) => (
        <Button
          key={year}
          variant={year === grid.fiscalYear ? 'default' : 'outline'}
          aria-pressed={year === grid.fiscalYear}
          onClick={() => onSelect(year)}
        >
          {year}
        </Button>
      ))}
    </div>
  );
}

/** The cash flow of the chosen year, with the one-time correction to the nearest year held. */
function useCashFlowView() {
  const [fiscalYear, setFiscalYear] = useState(() => new Date().getUTCFullYear());
  /*
   * The first request has to name a year before the server can say which years exist, so it names
   * the current one. When the ledger turns out not to hold it, the year is corrected ONCE - the
   * guard is what stops a ledger with no years at all from refetching forever.
   */
  const [yearCorrected, setYearCorrected] = useState(false);

  const request = useCallback(
    () => apiClient.GET('/v1/finance/cash-flow', { params: { query: { year: fiscalYear } } }),
    [fiscalYear],
  );
  const view = useFinanceView(request, isCashFlow);

  useEffect(() => {
    if (view.status !== 'ready' || yearCorrected) {
      return;
    }
    const years = view.data.availableYears;
    if (years.length === 0 || years.includes(view.data.fiscalYear)) {
      return;
    }
    const nearest = years.reduce((closest, year) =>
      Math.abs(year - fiscalYear) < Math.abs(closest - fiscalYear) ? year : closest,
    );
    setYearCorrected(true);
    setFiscalYear(nearest);
  }, [view, fiscalYear, yearCorrected]);
  const selectYear = (year: number): void => {
    setYearCorrected(true);
    setFiscalYear(year);
  };
  return { view, selectYear };
}

export function FinanceCashFlowPage() {
  const { t, i18n } = useTranslation('finance');
  const locale = resolveFinanceLocale(i18n.resolvedLanguage);
  const { view, selectYear } = useCashFlowView();

  if (view.status === 'loading') {
    return <CardsSkeleton />;
  }

  if (view.status === 'error') {
    return (
      <div className="p-6">
        <p className="text-sm">{t('cashFlow.loadError')}</p>
        <Button className="mt-3" onClick={view.reload}>
          {t('dashboard.retry')}
        </Button>
      </div>
    );
  }

  const grid = view.data;
  const labels = monthLabels(locale);
  const amount = (valueMinor: number) => formatAmountMinor(valueMinor, locale);

  return (
    <section className="p-6">
      <h2 className="screen-title">{t('cashFlow.title')}</h2>
      <p className="text-foreground/60 mt-1 text-sm">{t('cashFlow.subtitle')}</p>

      <YearSelector grid={grid} onSelect={selectYear} />

      {grid.availableYears.length === 0 ? (
        <p className="mt-6 text-sm">{t('cashFlow.empty')}</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-5xl text-sm">
            <caption className="sr-only">
              {t('cashFlow.tableCaption', { year: grid.fiscalYear })}
            </caption>
            <GridHead grid={grid} labels={labels} />
            <tbody>
              <InflowRows grid={grid} labels={labels} amount={amount} />
              {renderLine(labels, amount, t('cashFlow.totals.inflow'), grid.totals.inflow, true)}

              <OutflowRows grid={grid} labels={labels} amount={amount} />

              {renderLine(
                labels,
                amount,
                t('cashFlow.totals.operatingOutflow'),
                grid.totals.operatingOutflow,
                true,
              )}
              {renderLine(
                labels,
                amount,
                t('cashFlow.totals.nonEconomicOutflow'),
                grid.totals.nonEconomicOutflow,
                false,
              )}
              {renderLine(labels, amount, t('cashFlow.totals.outflow'), grid.totals.outflow, true)}
              {renderLine(labels, amount, t('cashFlow.totals.netFlow'), grid.totals.netFlow, true)}

              <BalanceRow grid={grid} labels={labels} amount={amount} />
            </tbody>
          </table>
        </div>
      )}

      <BalanceNotes grid={grid} amount={amount} />
      <p className="text-foreground/60 mt-1 text-xs">{t('cashFlow.closingNote')}</p>
    </section>
  );
}
